import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Instagram, ArrowUpRight } from "lucide-react";
import { getConsent, saveConsent } from "@/consent";
import PageHero from "@/components/PageHero";
import { Reveal, PageShell, EASE } from "@/components/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Kontakt() {
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [status, setStatus] = useState("idle");
  const [consent, setConsent] = useState(getConsent());

  useEffect(() => {
    const onChange = () => setConsent(getConsent());
    window.addEventListener("consent-changed", onChange);
    return () => window.removeEventListener("consent-changed", onChange);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/kontakt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("send failed");
      setStatus("success");
      setForm({ name: "", email: "", message: "", website: "" });
    } catch {
      setStatus("error");
    }
  };

  const field =
    "w-full border-b border-anthrazit/25 bg-transparent py-3 text-base text-anthrazit placeholder:text-anthrazit/40 transition-colors duration-300 focus:border-rot focus:outline-none";

  return (
    <PageShell testid="kontakt-page">
      <PageHero title="Kontakt" testid="kontakt-hero" />

      <section data-testid="kontakt-content" className="px-6 pb-20 md:px-12 md:pb-28">
        <div className="mx-auto grid max-w-5xl items-center gap-12 md:grid-cols-2 md:gap-14">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.1, ease: EASE }}
            className="relative mx-auto w-full max-w-sm"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-3 rounded-[2.5rem] border border-salbei/30"
            />
            <div
              data-testid="kontakt-portrait"
              className="relative overflow-hidden rounded-[2.5rem]"
            >
              <motion.img
                src="/images/carmen-pferd.webp"
                alt="Portrait von Carmen in rot-blauem Kleid vor einer Wand mit Blattschatten"
                className="block h-auto w-full"
                initial={{ scale: 1.06 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: EASE }}
              />
            </div>
          </motion.div>

          <Reveal>
            <div
              data-testid="kontakt-daten"
              className="rounded-[2rem] bg-karte p-8 md:p-10"
            >
              <p className="font-serif text-3xl text-rot md:text-4xl">Carmen Hackradt</p>
              <ul className="mt-7 space-y-5 text-base text-anthrazit/85">
                <li className="flex items-start gap-4">
                  <MapPin size={19} strokeWidth={1.5} className="mt-1 shrink-0 text-rot" />
                  <span>
                    Sehestedterstr. 10
                    <br />
                    24340 Eckernförde
                  </span>
                </li>
                <li className="flex items-center gap-4">
                  <Phone size={19} strokeWidth={1.5} className="shrink-0 text-rot" />
                  <a
                    href="tel:+491628564347"
                    data-testid="kontakt-telefon-link"
                    className="transition-colors duration-300 hover:text-rot"
                  >
                    0162 8564347
                  </a>
                </li>
                <li className="flex items-center gap-4">
                  <Mail size={19} strokeWidth={1.5} className="shrink-0 text-rot" />
                  <a
                    href="mailto:kontakt@carmenhackradt.de"
                    data-testid="kontakt-email-link"
                    className="break-all transition-colors duration-300 hover:text-rot"
                  >
                    kontakt@carmenhackradt.de
                  </a>
                </li>
                <li className="flex items-center gap-4">
                  <Instagram size={19} strokeWidth={1.5} className="shrink-0 text-rot" />
                  <a
                    href="https://instagram.com/seelenpfad_carmen_hackradt"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="kontakt-instagram-link"
                    className="break-all transition-colors duration-300 hover:text-rot"
                  >
                    seelenpfad_carmen_hackradt
                  </a>
                </li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="kontakt-formular-section" className="px-6 pb-20 md:px-12 md:pb-28">
        <Reveal>
          <form
            data-testid="kontakt-formular"
            onSubmit={submit}
            className="mx-auto max-w-2xl rounded-[2rem] bg-karte p-8 md:p-12"
          >
            <h2 className="font-serif text-3xl text-rot md:text-4xl">Kontaktanfrage</h2>
            <div className="mt-9 space-y-7">
              <input
                type="text"
                name="website"
                data-testid="kontakt-honeypot"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
              />
              <div>
                <label
                  htmlFor="kontakt-name"
                  className="mb-1 block text-xs uppercase tracking-[0.25em] text-anthrazit/60"
                >
                  Name
                </label>
                <input
                  id="kontakt-name"
                  data-testid="kontakt-name-input"
                  type="text"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={field}
                  placeholder="Dein Name"
                />
              </div>
              <div>
                <label
                  htmlFor="kontakt-email"
                  className="mb-1 block text-xs uppercase tracking-[0.25em] text-anthrazit/60"
                >
                  E-Mail
                </label>
                <input
                  id="kontakt-email"
                  data-testid="kontakt-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={field}
                  placeholder="deine@email.de"
                />
              </div>
              <div>
                <label
                  htmlFor="kontakt-nachricht"
                  className="mb-1 block text-xs uppercase tracking-[0.25em] text-anthrazit/60"
                >
                  Nachricht
                </label>
                <textarea
                  id="kontakt-nachricht"
                  data-testid="kontakt-nachricht-input"
                  required
                  maxLength={3000}
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className={`${field} resize-none`}
                  placeholder="Deine Nachricht"
                />
              </div>
            </div>

            {status === "success" && (
              <p
                data-testid="kontakt-success-message"
                className="mt-7 rounded-xl bg-salbei/15 px-5 py-4 text-sm text-salbei"
              >
                Vielen Dank für deine Nachricht. Deine Anfrage wurde erfolgreich übermittelt.
              </p>
            )}
            {status === "error" && (
              <p
                data-testid="kontakt-error-message"
                className="mt-7 rounded-xl bg-rot/10 px-5 py-4 text-sm text-rot"
              >
                Die Nachricht konnte nicht gesendet werden. Bitte versuche es erneut.
              </p>
            )}

            <button
              type="submit"
              data-testid="kontakt-submit-button"
              disabled={status === "sending"}
              className="group mt-9 inline-flex items-center gap-3 rounded-full bg-rot px-9 py-3.5 text-sm tracking-[0.15em] text-creme transition-all duration-500 hover:bg-tanne disabled:opacity-60"
            >
              {status === "sending" ? "Wird gesendet …" : "Nachricht senden"}
              <ArrowUpRight
                size={16}
                strokeWidth={1.5}
                className="transition-transform duration-500 group-hover:rotate-45"
              />
            </button>
          </form>
        </Reveal>
      </section>

      <section data-testid="anfahrt-section" className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-5xl">
          <Reveal>
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
              <div>
                <h2 className="font-serif text-3xl text-rot md:text-4xl">Anfahrt</h2>
                <p className="mt-3 text-base text-anthrazit/75">
                  Sehestedterstr. 10, 24340 Eckernförde
                </p>
              </div>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Sehestedterstr.+10,+24340+Eckernf%C3%B6rde"
                target="_blank"
                rel="noreferrer"
                data-testid="anfahrt-link"
                className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.25em] text-rot"
              >
                <span className="border-b border-rot/40 pb-1 transition-colors duration-500 group-hover:border-rot">
                  In Google Maps öffnen
                </span>
                <ArrowUpRight
                  size={16}
                  strokeWidth={1.5}
                  className="transition-transform duration-500 group-hover:rotate-45"
                />
              </a>
            </div>
            <div className="mt-8 overflow-hidden rounded-[2rem]">
              {consent?.external ? (
                <iframe
                  title="Karte: Sehestedterstr. 10, 24340 Eckernförde"
                  data-testid="anfahrt-karte"
                  src="https://www.google.com/maps?q=Sehestedterstr.+10,+24340+Eckernf%C3%B6rde&output=embed"
                  className="h-[380px] w-full border-0 md:h-[440px]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              ) : (
                <div
                  data-testid="maps-placeholder"
                  className="flex h-[380px] flex-col items-center justify-center gap-5 bg-taupe/15 px-8 text-center md:h-[440px]"
                >
                  <MapPin size={26} strokeWidth={1.5} className="text-rot" />
                  <p className="max-w-md text-sm leading-relaxed text-anthrazit/75">
                    Diese Karte wird von Google Maps bereitgestellt. Zum Anzeigen der Karte wird
                    eine Verbindung zu Google hergestellt.
                  </p>
                  <button
                    type="button"
                    data-testid="maps-load-button"
                    onClick={() => saveConsent(true)}
                    className="rounded-full bg-rot px-7 py-2.5 text-sm tracking-[0.1em] text-creme transition-colors duration-300 hover:bg-tanne"
                  >
                    Google Maps laden
                  </button>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
